<script setup>
import { computed, inject, ref, shallowRef, watch } from 'vue'
import { useApi } from '@provise/composables/api'
import { useConfigsStore } from '@/stores/configs'
import Form from '@provise/components/form.vue'
import SimpleSchema from '@/components/simpleSchema.vue'
import Step from './step.vue'
import Uploads from './uploads.vue'
import { computedWithControl } from '@vueuse/core'
import { commons } from '@provise/lib/transformer'
import DOMPurify from 'dompurify'

defineProps({
  edit: Boolean,
  editPermission: {
    type: Boolean,
    default: true,
  },
})
const emit = defineEmits(['save', 'refresh'])
const model = defineModel({
  get: v => {
    let _v = { ...v }
    if (v.medicine) {
      _v = { ..._v, medicine: mapMedicine(v.medicine) }
    }
    if (!v.uploads) {
      _v.uploads = []
    }
    return _v
  },
})
const customerCategories = shallowRef([])
const departments = ref([])
const department = computed(() => departments.value.find(d => model.value?.department?.id == d.id))
const show_registration_post = computed(() => model.value?.ur_name?.length > 0)
const customerCategorySchema = computed(() => {
  const v = customerCategories.value.find(c => c.id == model.value?.name_type?.id)?.display
  return v
})

const configStore = useConfigsStore()
const channelOptions = configStore.get('complaints.channel', [])
const countriesOptions = configStore.get('complaints.registration_country', [])

useApi({
  url: 'customer_category',
  model: customerCategories,
  pick: pickCustomerCategories,
  immediate: true,
})

useApi({
  url: 'departments',
  model: departments,
  pick: d => d.data || [],
  immediate: true,
})

const { refresh: clear_pd } = useApi({
  url: computed(() => `complaints/${model.value.id}/clear_pd`),
})

function pickCustomerCategories(d) {
  return (d.data || [])
    .filter(cc => cc.display)
    .map(m => {
      return {
        ...m,
        label: m.parent ? `${m.parent.name} / ${m.name}` : m.name,
      }
    })
}
function mapMedicine(m) {
  return {
    ...m,
    label: commons.medicineLabel(m),
  }
}
const sch = computedWithControl(
  () => [
    model.value,
    countriesOptions.value,
    channelOptions.value,
    customerCategories.value,
    customerCategorySchema.value,
    departments.value,
    department.value,
  ],
  () => {
    return sch1
  }
)
const sch1 = [
  ['text', 'name', 'ФИО инициатора обращения'],
  ['text', 'phone', 'Телефон'],
  ['text', 'email', 'E-mail'],
  ['text', 'city', 'Город'],
  ['checkbox', 'market_appeal', 'Обращение по фронтьерному рынку', { layout: 'g-start-1' }],
  ['select-id', 'registration_country', 'Страна', { options: countriesOptions, values: 'value' }],
  [
    'select-id',
    'name_type',
    'Выберите категорию инициатора обращения',
    { options: customerCategories, searchEnabled: false, values: 'label', layout: 'g-full' },
  ],
  [
    'text',
    'ur_name',
    'Наименование организации',
    {
      'show-if': [
        () => Boolean(customerCategorySchema.value != '[]' && model.value?.name_type?.id),
      ],
    },
  ],
  [
    'text',
    'registration_post',
    'Должность',
    {
      'show-if': [
        () =>
          Boolean(
            show_registration_post.value &&
              customerCategorySchema.value != '[]' &&
              model.value?.name_type?.id
          ),
      ],
    },
  ],
  [
    'select-medicine',
    'medicine',
    'Лекарственный препарат, дозировка, форма выпуска',
    { layout: 'g-col-2 g-start-1' },
  ],
  ['text', 'series', 'Серия'],
  [
    'select-id',
    'channel',
    'Канал получения претензии',
    { options: channelOptions, values: 'value' },
  ],
  ['checkbox', 'agreement', 'Согласие на обработку ПД', { layout: 'g-full' }],
  [
    'editor',
    'description',
    'Исходное описание претензии/обращения',
    { layout: 'g-full', rows: 10 },
  ],
  [
    'div',
    null,
    null,
    { class: 'card', layout: 'g-full' },
    ['div', null, null, { class: 'card-header', text: 'Работа со сторонними службами' }],
    [
      'div',
      null,
      null,
      { class: 'card-body' },
      [
        'select-id',
        'department',
        'Подразделение, на которое будет отправлена информация об обращении',
        { options: departments, searchEnabled: false, layout: 'mb-3' },
      ],
      [
        'div',
        '__department',
        null,
        {
          text: computed(
            () =>
              `Вы подтверждаете передачу обращения в «${department.value.name}» на e-mail ${department.value.email}`
          ),
          'show-if': [() => Boolean(department.value)],
          layout: 'mb-3',
        },
      ],
      [
        'checkbox',
        'close',
        'Закрыть обращение после передачи',
        {
          disabled: computed(() =>
            Boolean(!['registration', 'correction'].includes(model.value?.status?.key))
          ),
        },
      ],
    ],
  ],
]
const isch = {
  name_type: ['select-id', 'name_type.name'],
  channel: ['select-id', 'channel.value'],
  agreement: [null, mv => (mv.agreement ? 'Да' : 'Нет')],
  department: [null, 'department.name'],
  medicine: [null, mv => commons.medicineLabel(mv.medicine)],
  registration_country: [null, mv => mv?.registration_country?.value],
  description: [null, mv => [mv.description]],
}
const f = ref(null)
const isEditing = shallowRef(false)
const apiResource = inject('apiResource')

function my_sanitize(html) {
  return DOMPurify.sanitize(html)
}

function onAutosave(hook) {
  return async function (isNew) {
    if (isNew) {
      await f.value.reset()
      await f.value.edit()
    }
    hook()
  }
}

function onAddFiles(hook) {
  if (!model.value.id) {
    emit('save', onAutosave(hook), true)
  } else {
    hook()
  }
}

async function clearPersonalData() {
  if (
    confirm(
      'Данное действие удалит все персональные данные из обращения и истории. Восстановление будет невозможно. Подтвердите удаление'
    )
  ) {
    await clear_pd()
    emit('refresh')
  }
}
</script>
<template>
  <Step :edit="edit" v-model="model" :sch="sch" :isch="isch" name="registration">
    <Form
      entity-name="complaints"
      :entity="model"
      :entity-rbac="['common']"
      :edit-permission="editPermission || !model.id"
      :edit-label="model.sla_users?.registration ? null : 'Взять в работу'"
      @form:apply="$emit('save', () => f.reset())"
      @form:editable="isEditing = $event"
      @form:reset="apiResource('get')"
      ref="f"
      sticky-controls
    >
      <div v-if="model.original_email" class="card w-100 mb-3">
        <div class="card-header">
          <b>Оригинальное обращение клиента с email {{ model.email }}</b>
        </div>
        <div class="card-body">
          <b>Тема письма: {{ model.original_subject }}</b>
          <div v-html="my_sanitize(model?.original_email)"></div>
        </div>
      </div>
      <div class="form-schema mb-3">
        <SimpleSchema :simplsch="sch" v-model="model" />
      </div>
      <div v-if="!model.id" class="alert alert-light">
        <i class="bi bi-file-earmark" />
        Чтобы приложить файлы, сохраните обращение
      </div>
      <Uploads
        v-if="model.id"
        @add-files="onAddFiles"
        v-model="model"
        division="complaint.registration"
      />
      <slot name="sla" />
      <template #post-controls>
        <slot name="form-post-controls" v-if="!isEditing" />
        <div class="me-auto"></div>
        <button
          type="button"
          class="btn btn-outline-danger"
          @click="clearPersonalData"
          v-if="model.id"
        >
          <x-icon icon="person-circle-xmark" />
          Удалить ПДн
        </button>
      </template>
    </Form>
  </Step>
</template>
